import { PayloadAction, createSelector, createSlice } from "@reduxjs/toolkit";
import { DashboardAnalysisAnnotationType, DashboardAnalysisState, SavedSearch } from "./dashboardAnalysisTypes";
import { savedSearchApi } from "../../app/apislices/savedSearchApi";
import { AppDispatch, RootState } from "../../app/store";
import { setSelected } from "../../features/customAutocomplete/customAutocompleteSlice";
import { savedViewApi } from "../../app/apislices/savedViewApi";
import { GridFilterModel, GridRowId, GridRowSelectionModel, GridSortModel } from "@mui/x-data-grid-premium";
import { PostMeasurementExportGetApiArg, measurementExportApi } from "../../app/apislices/measurementExportApi";
import { MeasurementLogDto, PqEventExtendedDto } from "../../app/apislices/measurementApi";
import { measuringPointApi } from "../../app/apislices/measuringPointApi";
import { dataFieldApi } from "../../app/apislices/dataFieldApi";
import { MarkedMeasurementLogDto } from "../../app/apislices/markedMeasurementLogApi";
import dayjs from "dayjs";

const initialState: DashboardAnalysisState = {
  isSavedViewsDialogOpen: false,
  isSavedViewNameDialogOpen: false,
  isUnsavedTabsDialogOpen: false,
  isTabbarExpanded: true,
  isParameterPanelOpen: true,
  isRecordingsDialogOpen: false,
  isMeasuringPointInfoDialogOpen: false,
  selectedStart: dayjs().add(-1, "hour").toISOString(),
  selectedEnd: new Date().toISOString(),
  includedAnnotations: [],
  tabs: [
    {
      id: "unsaved_0",
      measuringPoint: null,
      dataFieldCategory: null,
      start: "",
      end: "",
      dataFields: [],
      includedAnnotations: [],
    },
  ],
  tabForEdit: "unsaved_0",
  tabEnumerator: 1,
  isLoadingTabbar: false,
  numberOfGraphColumns: 1,
  tabSelectionModel: [],
  tabSortModel: [],
  tabFilterModel: { items: [] },
  isTabExportDialogOpen: false,
  tabExportSelectionModel: [],
  tabExportSortModel: [],
  tabExportFilterModel: { items: [] },
  recordingsSelectionModel: [],
  recordingsSortModel: [{ field: "end", sort: "desc" }],
  recordingsFilterModel: { items: [] },
};

const dashboardAnalysisSlice = createSlice({
  name: "dashboardAnalysisSlice",
  initialState,
  reducers: {
    setSavedViewsDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.isSavedViewsDialogOpen = action.payload;
    },
    setSavedViewNameDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.isSavedViewNameDialogOpen = action.payload;
    },
    setUnsavedTabsDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.isUnsavedTabsDialogOpen = action.payload;
    },
    setTabbarExpanded: (state, action: PayloadAction<boolean>) => {
      state.isTabbarExpanded = action.payload;
    },
    setParameterPanelOpen: (state, action: PayloadAction<boolean>) => {
      state.isParameterPanelOpen = action.payload;
    },
    setIsRecordingsDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.isRecordingsDialogOpen = action.payload;
    },
    setMeasuringPointInfoDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.isMeasuringPointInfoDialogOpen = action.payload;
    },
    setSelectedStart: (state, action: PayloadAction<string>) => {
      state.selectedStart = action.payload;
    },
    setSelectedEnd: (state, action: PayloadAction<string>) => {
      state.selectedEnd = action.payload;
    },
    addTab: (state, action: PayloadAction<SavedSearch>) => {
      if (!state.tabs.find((tab) => tab.id === action.payload.id)) {
        const newTab = {
          ...action.payload,
          id: action.payload.id === "unsaved" ? "unsaved_" + state.tabEnumerator++ : action.payload.id,
        };
        state.tabs.push(newTab);
        state.tabForEdit = newTab.id;
      }
    },
    updateTab: (state, action: PayloadAction<SavedSearch>) => {
      const tab = state.tabs.find((x) => x.id === action.payload.id);
      if (tab) {
        tab.measuringPoint = action.payload.measuringPoint;
        tab.dataFieldCategory = action.payload.dataFieldCategory;
        tab.start = action.payload.start;
        tab.end = action.payload.end;
        tab.dataFields = action.payload.dataFields;
        tab.includedAnnotations = action.payload.includedAnnotations;
      }
    },
    updateTabId: (state, action: PayloadAction<{ id: string; newId: string }>) => {
      const tab = state.tabs.find((x) => x.id === action.payload.id);
      if (tab) {
        tab.id = action.payload.newId;
      }
    },
    deleteTab: (state, action: PayloadAction<string>) => {
      state.tabs = state.tabs.filter((x) => x.id !== action.payload);
    },
    duplicateTab: (state, action: PayloadAction<string>) => {
      const tab = state.tabs.find((x) => x.id === action.payload);
      if (tab) {
        state.tabs.push({ ...tab, id: "unsaved_" + state.tabEnumerator++ });
      }
    },
    duplicateSelectedTab: (state) => {
      if (state.tabSelectionModel.length > 0) {
        state.tabSelectionModel.forEach((id) => {
          const tab = state.tabs.find((x) => x.id === id);
          if (tab) {
            state.tabs.push({ ...tab, id: "unsaved_" + state.tabEnumerator++ });
          }
        });
      } else {
        const tab = state.tabs.find((x) => x.id === state.tabForEdit);
        if (tab) {
          state.tabs.push({ ...tab, id: "unsaved_" + state.tabEnumerator++ });
        }
      }
    },
    setTabForEdit: (state, action: PayloadAction<string>) => {
      state.tabForEdit = action.payload;
    },
    clearTabs: (state) => {
      state.tabs = [];
    },
    setIsLoadingTabbar: (state, action: PayloadAction<boolean>) => {
      state.isLoadingTabbar = action.payload;
    },
    setTabSelectionModel: (state, action: PayloadAction<GridRowSelectionModel>) => {
      state.tabSelectionModel = action.payload;
    },
    addToTabSelectionModel: (state, action: PayloadAction<GridRowId>) => {
      if (!state.tabSelectionModel.includes(action.payload)) state.tabSelectionModel.push(action.payload);
    },
    setNumberOfGraphColumns: (state, action: PayloadAction<number>) => {
      state.numberOfGraphColumns = action.payload;
    },
    setIncludedAnnotations: (state, action: PayloadAction<DashboardAnalysisAnnotationType[]>) => {
      state.includedAnnotations = action.payload;
    },
    toggleAnnotation: (state, action: PayloadAction<DashboardAnalysisAnnotationType>) => {
      if (state.includedAnnotations.includes(action.payload)) {
        state.includedAnnotations = state.includedAnnotations.filter((x) => x !== action.payload);
      } else {
        state.includedAnnotations.push(action.payload);
      }
    },
    setTabSortModel: (state, action: PayloadAction<GridSortModel>) => {
      state.tabSortModel = action.payload;
    },
    setTabFilterModel: (state, action: PayloadAction<GridFilterModel>) => {
      state.tabFilterModel = action.payload;
    },
    setIsTabExportDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.isTabExportDialogOpen = action.payload;
    },
    setTabExportSelectionModel: (state, action: PayloadAction<GridRowSelectionModel>) => {
      state.tabExportSelectionModel = action.payload;
    },
    setTabExportSortModel: (state, action: PayloadAction<GridSortModel>) => {
      state.tabExportSortModel = action.payload;
    },
    setTabExportFilterModel: (state, action: PayloadAction<GridFilterModel>) => {
      state.tabExportFilterModel = action.payload;
    },
    setRecordingsSortModel: (state, action: PayloadAction<GridSortModel>) => {
      state.recordingsSortModel = action.payload;
    },
    setRecordingsFilterModel: (state, action: PayloadAction<GridFilterModel>) => {
      state.recordingsFilterModel = action.payload;
    },
    setRecordingsSelectionModel: (state, action: PayloadAction<GridRowSelectionModel>) => {
      state.recordingsSelectionModel = action.payload;
    },
  },
});

export const selectTabData = createSelector([(state) => state.dashboardAnalysis.tabs, (state, tabId) => tabId], (tabs, tabId) =>
  tabs.find((x) => x.id === tabId)
);

export const {
  setSavedViewsDialogOpen,
  setSavedViewNameDialogOpen,
  setUnsavedTabsDialogOpen,
  setTabbarExpanded,
  setParameterPanelOpen,
  setIsRecordingsDialogOpen,
  setMeasuringPointInfoDialogOpen,
  setSelectedStart,
  setSelectedEnd,
  addTab,
  updateTab,
  updateTabId,
  deleteTab,
  duplicateTab,
  duplicateSelectedTab,
  setTabForEdit,
  clearTabs,
  setIsLoadingTabbar,
  setTabSelectionModel,
  addToTabSelectionModel,
  setNumberOfGraphColumns,
  setIncludedAnnotations,
  toggleAnnotation,
  setTabSortModel,
  setTabFilterModel,
  setIsTabExportDialogOpen,
  setTabExportSelectionModel,
  setTabExportSortModel,
  setTabExportFilterModel,
  setRecordingsSortModel,
  setRecordingsFilterModel,
  setRecordingsSelectionModel,
} = dashboardAnalysisSlice.actions;

export default dashboardAnalysisSlice.reducer;

measurementExportApi.enhanceEndpoints({
  endpoints: {
    postMeasurementExportGet: {
      transformResponse: (response: string, meta: unknown, args: PostMeasurementExportGetApiArg) => {
        const link = document.createElement("a");
        link.href = response;
        document.body.appendChild(link);
        link.click();
        link.remove();
        return "";
      },
      query: (queryArg) => ({
        url: `/MeasurementExport/Get`,
        method: "POST",
        body: queryArg.body,
        params: {
          from: queryArg["from"],
          to: queryArg.to,
          measuringPoint: queryArg.measuringPoint,
          manufacturer: queryArg.manufacturer,
          dataClass: queryArg.dataClass,
          mailAddress: queryArg.mailAddress,
        },
        responseHandler: (response) => response.text(),
        cache: "no-cache",
      }),
    },
  },
});

export const addEmptyTab = () => (dispatch: AppDispatch) => {
  const tab: SavedSearch = {
    id: "unsaved",
    measuringPoint: null,
    dataFieldCategory: null,
    start: "",
    end: "",
    dataFields: [],
    includedAnnotations: [],
  };
  dispatch(addTab(tab));
};

export const deleteTabById = (id: string) => (dispatch: AppDispatch, getState: () => RootState) => {
  const state = getState();
  const tabCount = state.dashboardAnalysis.tabs.length;
  const tabIndex = state.dashboardAnalysis.tabs.findIndex((x) => x.id === id);

  dispatch(deleteTab(id));
  dispatch(openTab(state.dashboardAnalysis.tabs[tabIndex === tabCount - 1 ? tabCount - 2 : tabIndex + 1]));

  if (tabCount === 1) {
    dispatch(addEmptyTab());
    dispatch(openTab(state.dashboardAnalysis.tabs[0]));
  }
};

export const deleteSelectedTab = () => (dispatch: AppDispatch, getState: () => RootState) => {
  const state = getState();
  const tabCount = state.dashboardAnalysis.tabs.length;
  const selectedCount = state.dashboardAnalysis.tabSelectionModel.length;

  if (selectedCount > 0) {
    state.dashboardAnalysis.tabSelectionModel.forEach((x) => dispatch(deleteTab(x)));
    dispatch(openTab(state.dashboardAnalysis.tabs[0]));
  } else {
    const tabIndex = state.dashboardAnalysis.tabs.findIndex((x) => x.id === state.dashboardAnalysis.tabForEdit);

    dispatch(deleteTab(state.dashboardAnalysis.tabForEdit));
    dispatch(openTab(state.dashboardAnalysis.tabs[tabIndex === tabCount - 1 ? tabCount - 2 : tabIndex + 1]));
  }
  if (tabCount === 1 || tabCount === selectedCount) {
    dispatch(addEmptyTab());
    dispatch(openTab(state.dashboardAnalysis.tabs[0]));
  }
};

export const openTab = (tab: SavedSearch) => (dispatch: AppDispatch) => {
  dispatch(setSelected(["dashboardAnalysisMeasuringPoints", tab.measuringPoint]));
  dispatch(setSelected(["dashboardAnalysisDataFieldCategories", tab.dataFieldCategory]));
  dispatch(setSelectedStart(tab.start === "" ? dayjs().add(-1, "hour").toISOString() : tab.start));
  dispatch(setSelectedEnd(tab.end === "" ? new Date().toISOString() : tab.end));
  dispatch(setSelected(["dashboardAnalysisDataFields", tab.dataFields]));
  dispatch(setIncludedAnnotations(tab.includedAnnotations));
  dispatch(setTabForEdit(tab.id));
  dispatch(setParameterPanelOpen(true));
};

export const saveParametersToTab = () => (dispatch: AppDispatch, getState: () => RootState) => {
  const state = getState();

  const tabForEdit = state.dashboardAnalysis.tabs.find((x) => x.id === state.dashboardAnalysis.tabForEdit);
  if (tabForEdit) {
    dispatch(
      updateTab({
        ...tabForEdit,
        measuringPoint: state.customAutocomplete.instances["dashboardAnalysisMeasuringPoints"].selected,
        dataFieldCategory: state.customAutocomplete.instances["dashboardAnalysisDataFieldCategories"].selected,
        start: state.dashboardAnalysis.selectedStart,
        end: state.dashboardAnalysis.selectedEnd,
        dataFields: state.customAutocomplete.instances["dashboardAnalysisDataFields"].selected,
        includedAnnotations: state.dashboardAnalysis.includedAnnotations,
      })
    );
    dispatch(addToTabSelectionModel(tabForEdit.id));
  }
};

export const addSelectedRecordingsAsTabs =
  (recordings: MeasurementLogDto[]) => (dispatch: AppDispatch, getState: () => RootState) => {
    if (recordings.length === 0) return;

    const state = getState();

    const measuringPointsPromise = dispatch(measuringPointApi.endpoints.getMeasuringPoint.initiate());
    const dataFieldCategoriesPromise = dispatch(
      dataFieldApi.endpoints.getDataFieldCategoryByMeasuringPoint.initiate(recordings[0].measuringPoint)
    );
    const dataFieldsPromise = dispatch(
      dataFieldApi.endpoints.getDataField.initiate({
        measuringPoint: recordings[0].measuringPoint,
      })
    );
    Promise.all([measuringPointsPromise.unwrap(), dataFieldCategoriesPromise.unwrap(), dataFieldsPromise.unwrap()]).then(
      ([measuringPoints, dataFieldCategories, dataFields]) => {
        recordings.forEach((recording) => {
          const dataFieldCategory = dataFieldCategories.find((x) => x.dataClass.startsWith(recording.dataClassName))!;
          const tab: SavedSearch = {
            id: "unsaved",
            measuringPoint: measuringPoints.find((x) => x.name === recording.measuringPoint)!,
            dataFieldCategory: dataFieldCategory,
            start: new Date(recording.start / 1000000).toISOString(),
            end: new Date(recording.end / 1000000 + 1).toISOString(), //add one millisecond to include measurements in sub-millisecond range after end
            dataFields: dataFields.filter(
              (x) =>
                [
                  "oscu1",
                  "oscu2",
                  "oscu3",
                  "oscu12",
                  "oscu23",
                  "oscu31",
                  "halfcycleu1",
                  "halfcycleu2",
                  "halfcycleu3",
                  "halfcycleu12",
                  "halfcycleu23",
                  "halfcycleu31",
                ].includes(x.name) && x.dataClass === dataFieldCategory.dataClass //TODO get from db
            ),
            includedAnnotations: [],
          };
          dispatch(addTab(tab));
        });
        measuringPointsPromise.unsubscribe();
        dataFieldCategoriesPromise.unsubscribe();
        dataFieldsPromise.unsubscribe();
      }
    );
  };

export const addSelectedEventsAsTabs = (events: PqEventExtendedDto[]) => (dispatch: AppDispatch, getState: () => RootState) => {
  if (events.length === 0) return;

  const state = getState();

  const measuringPointsPromise = dispatch(measuringPointApi.endpoints.getMeasuringPoint.initiate());
  const dataFieldCategoriesPromise = dispatch(
    dataFieldApi.endpoints.getDataFieldCategoryByMeasuringPoint.initiate(events[0].measuringPoint)
  );
  const dataFieldsPromise = dispatch(
    dataFieldApi.endpoints.getDataField.initiate({
      measuringPoint: events[0].measuringPoint,
    })
  );
  Promise.all([measuringPointsPromise.unwrap(), dataFieldCategoriesPromise.unwrap(), dataFieldsPromise.unwrap()]).then(
    ([measuringPoints, dataFieldCategories, dataFields]) => {
      events.forEach((event) => {
        const dataField = dataFields.find((x) => x.name === event.dataField)!;
        const tab: SavedSearch = {
          id: "unsaved",
          measuringPoint: measuringPoints.find((x) => x.name === event.measuringPoint)!,
          dataFieldCategory: dataFieldCategories.find(
            (x) => x.dataClass === dataField.dataClass && x.category === dataField.category
          )!,
          start: event.intervalStart!,
          end: event.intervalEnd!,
          dataFields: [dataField],
          includedAnnotations: [],
        };
        dispatch(addTab(tab));
      });
      measuringPointsPromise.unsubscribe();
      dataFieldCategoriesPromise.unsubscribe();
      dataFieldsPromise.unsubscribe();
    }
  );
};

export const addSelectedMarkedMeasurementLogEntriesAsTabs =
  (entries: MarkedMeasurementLogDto[]) => (dispatch: AppDispatch, getState: () => RootState) => {
    if (entries.length === 0) return;

    const state = getState();

    const measuringPointsPromise = dispatch(measuringPointApi.endpoints.getMeasuringPoint.initiate());
    const dataFieldCategoriesPromise = dispatch(
      dataFieldApi.endpoints.getDataFieldCategoryByMeasuringPoint.initiate(entries[0].measuringPoint!)
    );
    const dataFieldsPromise = dispatch(
      dataFieldApi.endpoints.getDataField.initiate({
        measuringPoint: entries[0].measuringPoint!,
      })
    );
    Promise.all([measuringPointsPromise.unwrap(), dataFieldCategoriesPromise.unwrap(), dataFieldsPromise.unwrap()]).then(
      ([measuringPoints, dataFieldCategories, dataFields]) => {
        entries.forEach((entry) => {
          const dataField = dataFields.find((x) => x.id === entry.dataFieldId)!;
          const tab: SavedSearch = {
            id: "unsaved",
            measuringPoint: measuringPoints.find((x) => x.name === entry.measuringPoint)!,
            dataFieldCategory: dataFieldCategories.find(
              (x) => x.dataClass === dataField.dataClass && x.category === dataField.category
            )!,
            start: new Date(entry.start! / 1000000).toISOString(),
            end: new Date(entry.end! / 1000000).toISOString(),
            dataFields: [dataField],
            includedAnnotations: [],
          };
          dispatch(addTab(tab));
        });
        measuringPointsPromise.unsubscribe();
        dataFieldCategoriesPromise.unsubscribe();
        dataFieldsPromise.unsubscribe();
      }
    );
  };

export const addSavedSearchesAsTabs =
  (ids: number[]) =>
  (dispatch: AppDispatch, getState: () => RootState): void => {
    const state = getState();

    dispatch(setIsLoadingTabbar(true));

    dispatch(clearTabs());

    const result = ids.map((x) => dispatch(savedSearchApi.endpoints.getSavedSearchById.initiate(x)));
    Promise.all(result.map((x) => x.unwrap())).then((savedSearches) => {
      savedSearches.forEach((savedSearch) => {
        dispatch(addTab({ ...JSON.parse(savedSearch.value), id: savedSearch.id.toString() }));
      });
      dispatch(setIsLoadingTabbar(false));
    });
    result.forEach((x) => x.unsubscribe());
  };

export const saveView = (name?: string) => (dispatch: AppDispatch, getState: () => RootState) => {
  const state = getState();

  let savedSearchIds = state.dashboardAnalysis.tabs.filter((tab) => !tab.id.startsWith("unsaved")).map((tab) => Number(tab.id));

  const result = state.dashboardAnalysis.tabs
    .filter((tab) => tab.dataFields.length > 0)
    .map((tab) =>
      tab.id.startsWith("unsaved")
        ? dispatch(
            savedSearchApi.endpoints.postSavedSearch.initiate({
              id: 0,
              name: "savedSearch",
              category: "savedSearch",
              isDefault: false,
              value: JSON.stringify(tab),
            })
          )
        : dispatch(
            savedSearchApi.endpoints.putSavedSearchById.initiate({
              id: Number(tab.id),
              configurationDto: {
                id: Number(tab.id),
                name: "savedSearch",
                category: "savedSearch",
                isDefault: false,
                value: JSON.stringify(tab),
              },
            })
          )
    );
  Promise.all(result.map((x) => x.unwrap())).then((savedSearches) => {
    savedSearchIds = savedSearchIds.concat(savedSearches.filter((x) => x));

    name
      ? dispatch(
          savedViewApi.endpoints.postSavedView.initiate({
            id: 0,
            name: name,
            value: JSON.stringify(savedSearchIds),
            category: "savedView",
            isDefault: false,
          })
        )
      : dispatch(
          savedViewApi.endpoints.putSavedViewById.initiate({
            id: state.customAutocomplete.instances["dashboardAnalysisSavedViews"].selected.id,
            configurationDto: {
              id: state.customAutocomplete.instances["dashboardAnalysisSavedViews"].selected.id,
              name: state.customAutocomplete.instances["dashboardAnalysisSavedViews"].selected.name,
              value: JSON.stringify(savedSearchIds),
              category: "savedView",
              isDefault: false,
            },
          })
        );
  });
};
